import { ean13Modules, toEan13 } from '@njpos/core';

export interface LabelItem { brand: string; name: string; size_ml: number; units_per_pack?: number; retail_cents: number; code: string | null; copies: number }
export type LabelTemplate = 'tag' | 'sheet';

export const TEMPLATES: Record<LabelTemplate, { name: string; hint: string }> = {
  tag: { name: 'Shelf tag, 2.25 × 1.25 in', hint: 'Label printers and thermal rolls. One label per page.' },
  sheet: { name: 'Sheet labels, 30 per page (Avery 5160)', hint: 'Letter-size sticky label sheets in any office printer.' },
};

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
const sizeText = (ml: number, pack?: number) => `${ml >= 1000 ? `${ml / 1000}L` : `${ml}ml`}${pack && pack > 1 ? ` · ${pack}-pack` : ''}`;
const price = (c: number) => `$${(c / 100).toFixed(2)}`;

/** An inline SVG of the barcode, or an empty string when the stored code cannot be drawn as EAN-13/UPC-A. */
export function barcodeSvg(code: string | null, heightPx = 34): string {
  const ean = code ? toEan13(code) : null;
  if (!ean) return '';
  const m = ean13Modules(ean);
  let rects = '';
  for (let i = 0; i < m.length; ) {
    if (m[i] === '1') { let j = i; while (m[j] === '1') j++; rects += `<rect x="${i + 9}" y="0" width="${j - i}" height="${heightPx}"/>`; i = j; } else i++;
  }
  return `<svg class="bc" viewBox="0 0 113 ${heightPx}" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg"><g fill="#000">${rects}</g></svg><div class="num">${ean}</div>`;
}

/** A complete printable page. The caller opens it in a window and calls print(). */
export function labelsHtml(items: LabelItem[], template: LabelTemplate): string {
  const labels = items.flatMap((it) => Array.from({ length: Math.max(1, it.copies) }, () => `
    <div class="label">
      <div class="name">${esc(it.brand)} ${esc(it.name)}</div>
      <div class="size">${esc(sizeText(it.size_ml, it.units_per_pack))}</div>
      <div class="price">${price(it.retail_cents)}</div>
      <div class="barcode">${barcodeSvg(it.code)}</div>
    </div>`));
  const page = template === 'tag'
    ? `@page { size: 2.25in 1.25in; margin: 0 }
       .label { width: 2.25in; height: 1.25in; padding: 0.07in 0.1in; page-break-after: always; }`
    : `@page { size: letter; margin: 0.5in 0.19in }
       body { display: grid; grid-template-columns: repeat(3, 2.625in); column-gap: 0.125in; grid-auto-rows: 1in; }
       .label { width: 2.625in; height: 1in; padding: 0.04in 0.1in; overflow: hidden; }`;
  const tag = template === 'tag';
  return `<!doctype html><html><head><meta charset="utf-8"><title>Shelf labels</title><style>
    ${page}
    * { box-sizing: border-box; }
    body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #000; }
    .label { position: relative; overflow: hidden; }
    .name { font-weight: 700; font-size: ${tag ? '11pt' : '8.5pt'}; line-height: 1.1; max-height: 2.2em; overflow: hidden; width: 68%; }
    .size { font-size: ${tag ? '8pt' : '7pt'}; margin-top: 1px; }
    .price { position: absolute; right: ${tag ? '0.1in' : '0.1in'}; top: ${tag ? '0.06in' : '0.03in'}; font-weight: 800; font-size: ${tag ? '22pt' : '15pt'}; }
    .barcode { position: absolute; left: 0.1in; right: 0.1in; bottom: ${tag ? '0.05in' : '0.02in'}; height: ${tag ? '0.5in' : '0.38in'}; text-align: center; }
    .bc { width: 70%; height: 72%; display: block; margin: 0 auto; }
    .num { font-size: 6.5pt; letter-spacing: 1px; }
    @media screen { body { padding: 12px; } .label { border: 1px dashed #aaa; margin: 0 0 6px; } }
  </style></head><body>${labels.join('')}</body></html>`;
}

export function printLabels(items: LabelItem[], template: LabelTemplate): boolean {
  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.open(); w.document.write(labelsHtml(items, template)); w.document.close();
  w.focus();
  setTimeout(() => w.print(), 250);
  return true;
}
